import React, { useEffect, useState } from "react";
import { topicDictionary } from "../Util/tileDictionary";
import { DEFAULT_YEAR } from "../Util/constants";
import MapView from "./Map";
import Graph from "./Graph";
import Table from "./Table";
import QuestionHeader from "./QuestionHeader";
import SampleSizeToggle from "./SampleSizeToggle";
import { GetQuestionDataAllLocations } from "../Util/Api";
import DataVisualizationToggle from "./DataVisualizationToggle";
import { GetComparison } from "../Util/Api";
import PrintPdf from "./TableButtons/PrintPdf";
import ExportCsv from "./TableButtons/ExportCsv";
import {
  GetQuestionOneLocation,
  GetSubQuestionAllLocations,
  GetYears,
  GetPatternsOverTime,
} from "../Util/Api";
import { useNavigate } from "react-router-dom";

import * as analytics from "../Util/Analytics/TableAnalytics";
import LoadingOverlay from "./LoadingOverlay";
import { ShowPValueColumn, EnableSubQuestionTextMatching } from "../Util/env";
import { isZeroToHundredCI, parseNumber, isSpecialCode, isCensusSite } from "../Util/SuppressionRules";

// Helper function to check if data contains specific suppression codes
function hasSuppressionCode(data, code) {
  if (!data) return false;
  
  const checkValue = (v) => {
    if (v === null || v === undefined) return false;
    return String(v).trim() === code;
  };
  
  // For array of rows
  if (Array.isArray(data)) {
    return data.some(row => 
      checkValue(row?.DataValue) || 
      checkValue(row?.DataValueRaw) || 
      checkValue(row?.NPercent) ||
      checkValue(row?.Percent)
    );
  }
  
  // For object with tableData property
  if (data?.tableData && Array.isArray(data.tableData)) {
    return data.tableData.some(row => 
      checkValue(row?.DataValue) || 
      checkValue(row?.DataValueRaw) || 
      checkValue(row?.NPercent) ||
      checkValue(row?.Percent)
    );
  }
  
  // For comparison data with Rows
  if (data?.Rows && Array.isArray(data.Rows)) {
    return data.Rows.some(row => 
      checkValue(row?.YearA?.DataValue) ||
      checkValue(row?.YearA?.DataValueRaw) ||
      checkValue(row?.YearA?.Percent) ||
      checkValue(row?.YearB?.DataValue) ||
      checkValue(row?.YearB?.DataValueRaw) ||
      checkValue(row?.YearB?.Percent)
    );
  }
  
  return false;
}

// Detect 0–100 suppression (or missing CI with value exactly 0/100)
function hasZeroHundredSuppression(data) {
  if (!data) return false;

  const extractRows = (d) => {
    if (Array.isArray(d)) return d;
    if (d?.tableData && Array.isArray(d.tableData)) return d.tableData;
    if (d?.Rows && Array.isArray(d.Rows)) return d.Rows;
    return [];
  };

  const rows = extractRows(data);

  const checkRow = (row) => {
    if (!row) return false;

    // Gather bounds from common CI fields
    const candidatesLower = [row.LowCI, row.LowValue, row.LowerValue, row.LCI, row.Lower, row.LowerBound];
    const candidatesUpper = [row.HighCI, row.HighValue, row.UpperValue, row.UCI, row.Upper, row.UpperBound];

    let lower = null, upper = null;
    for (let c of candidatesLower) {
      if (c !== undefined && c !== null && String(c).trim() !== "") {
        const n = parseNumber(c);
        if (n !== null && !isNaN(n)) { lower = n; break; }
      }
    }
    for (let c of candidatesUpper) {
      if (c !== undefined && c !== null && String(c).trim() !== "") {
        const n = parseNumber(c);
        if (n !== null && !isNaN(n)) { upper = n; break; }
      }
    }

    // Parse from ConfidenceInterval string when bounds absent
    if ((lower === null || upper === null) && row.ConfidenceInterval) {
      const m = String(row.ConfidenceInterval).match(/([0-9]+\.?[0-9]*)[^0-9]+([0-9]+\.?[0-9]*)/);
      if (m) {
        if (lower === null) lower = parseNumber(m[1]);
        if (upper === null) upper = parseNumber(m[2]);
      }
    }

    if (lower !== null && upper !== null) {
      if (isZeroToHundredCI(lower, upper)) return true;
    }

    // If no bounds but value is exactly 0 or 100, treat as suppressed
    if (lower === null && upper === null) {
      const val = row.NPercent ?? row.DataValue ?? row.DataValueRaw ?? row.Percent;
      const numVal = parseNumber(val);
      if (numVal !== null && !isNaN(numVal) && (numVal === 0 || numVal === 100)) return true;
    }

    return false;
  };

  // For comparison Rows, evaluate YearA and YearB as well
  return rows.some(r => {
    if (checkRow(r)) return true;
    if (r?.YearA && checkRow(r.YearA)) return true;
    if (r?.YearB && checkRow(r.YearB)) return true;
    return false;
  });
}

// Detect rows that display an em-dash CI (missing/placeholder CI) for non-suppressed values
// Useful to surface the em-dash footnote when such cases exist
function hasDashCI(data) {
  if (!data) return false;

  const extractRows = (d) => {
    if (Array.isArray(d)) return d;
    if (d?.tableData && Array.isArray(d.tableData)) return d.tableData;
    if (d?.Rows && Array.isArray(d.Rows)) return d.Rows;
    return [];
  };

  const rows = extractRows(data);

  const isDash = (ciVal) => {
    if (ciVal === null || ciVal === undefined) return true;
    const s = String(ciVal).trim();
    return s === "—" || s === "-" || s === "--" || s === "–" || s === "";
  };

  const checkRow = (row) => {
    if (!row) return false;

    // Skip suppression-code rows
    const valRaw = row.NPercent ?? row.DataValue ?? row.DataValueRaw ?? row.Percent;
    if (isSpecialCode(valRaw)) return false;
    const val = parseNumber(valRaw);

    // If value is 0/100, it will be handled by dagger suppression; don't count for dash footnote
    if (val !== null && !isNaN(val) && (val === 0 || val === 100)) return false;

    // Check CI bounds and ConfidenceInterval string for dash/missing
    const lowerCandidates = [row.LowCI, row.LowValue, row.LowerValue, row.LCI, row.Lower, row.LowValue, row.LowerBound, row.ConfidenceLower];
    const upperCandidates = [row.HighCI, row.HighValue, row.UpperValue, row.UCI, row.Upper, row.HighValue, row.UpperBound, row.ConfidenceUpper];

    let lower = null, upper = null;
    for (let c of lowerCandidates) {
      if (c !== undefined && c !== null && String(c).trim() !== "") {
        const n = parseNumber(c);
        if (n !== null && !isNaN(n)) { lower = n; break; }
      }
    }
    for (let c of upperCandidates) {
      if (c !== undefined && c !== null && String(c).trim() !== "") {
        const n = parseNumber(c);
        if (n !== null && !isNaN(n)) { upper = n; break; }
      }
    }

    // If bounds exist, don't treat as dash CI (even if CI string has dash)
    if (lower !== null && upper !== null) return false;

    // If bounds missing, rely on CI string or dash placeholders
    if (row.ConfidenceInterval && !isDash(row.ConfidenceInterval)) {
      // ConfidenceInterval exists and is not a dash placeholder; treat as non-dash
      return false;
    }

    return true;
  };

  return rows.some(r => {
    if (checkRow(r)) return true;
    if (r?.YearA && checkRow(r.YearA)) return true;
    if (r?.YearB && checkRow(r.YearB)) return true;
    return false;
  });
}

const DataVisualization = ({
  displayLocations,
  displayLocationDropdown,
  locations,
  onVizTypeChange,
  onYearChange,
  onLocationChange,
  selectedData,
  // Global sample size preferences are owned by App and
  // passed down so the choice persists across questions/routes.
  showSampleSize,
  setShowSampleSize,
  setDataLoading,
  // Info banner message for user notifications (e.g., location reset for map view)
  infoBannerMessage,
  setInfoBannerMessage,
}) => {
  const question = selectedData ? selectedData.question : null;
  const year = selectedData ? selectedData.year : null;
  const subQuestionValue = selectedData ? selectedData.subQuestionValue : null;
  const location = selectedData ? selectedData.location : null;
  const vizType = selectedData ? selectedData.vizType : null;
  const subQuestion = selectedData.subQuestion
    ? selectedData.subQuestion
    : selectedData.subQuestionValue
      ? selectedData.question.SubQuestions.find(
          (i) => String(i.SubQuestionId).toLowerCase() === String(selectedData.subQuestionValue).toLowerCase()
        )
      : null;
  const scrollToTableHeader = selectedData
    ? selectedData.scrollToTableHeader
    : null;

  let navigate = useNavigate();
  const [subQuestionCSVData, setSubQuestionCSVData] = useState(null);
  const [graphCsvData, setGraphCsvData] = useState(null);
  const [subQuestionTableData, setSubQuestionTableData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [footnotes, setFootnotes] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [subQuestionLoading, setSubQuestionLoading] = useState(false);
  const [isPatternsOverTime, setIsPatternsOverTime] = useState(false);
  const [shortNavFootNoteSymbol, setShortNavFootNoteSymbol] = useState("");
  const [
    locationSelectedFromDropdownMenu,
    setLocationSelectedFromDropdownMenu,
  ] = useState(false);

  useEffect(() => {
    if (!question) return;
    // Show loading overlay while fetching initial data
    setOverlayVisible(true);
    if (question.SubQuestions.length === 0 && location.LocationAbbr === "ALL") {
      setIsPatternsOverTime(false);
      getData(question.QuestionId, year || DEFAULT_YEAR || question.LatestYear);
    } else if (
      question.SubQuestions.length === 0 &&
      location.LocationAbbr !== "ALL"
    ) {
      setIsPatternsOverTime(true);
      fetchPatternsOverTime();
    } else if (
      question.SubQuestions.length === 0 &&
      location.LocationAbbr !== "ALL" &&
      selectedData.subQuestionValue !== null
    ) {
      setIsPatternsOverTime(true);
      fetchPatternsOverTime();
    } else if (
      question.SubQuestions.length > 0 &&
      location.LocationAbbr !== "ALL" &&
      subQuestionValue
    ) {
      // For specific locations with subquestions AND a subQuestion selected,
      // show patterns over time view (View #3)
      setIsPatternsOverTime(true);
      fetchPatternsOverTime();
    } else if (
      question.SubQuestions.length > 0 &&
      location.LocationAbbr !== "ALL" &&
      !subQuestionValue
    ) {
      // For specific locations with subquestions but NO subQuestion selected,
      // show View #2 (not POT) - this allows the "+ Compare second location/year" link
      setIsPatternsOverTime(false);
      // Fetch the subquestion list for the location, filtered by the selected year
      // Use fetchTableDataForLocation instead of fetchPatternsOverTime to ensure year filtering
      fetchTableDataForLocation(
        location.LocationAbbr,
        questionId,
        year || DEFAULT_YEAR || question.LatestYear
      );
    } else if (subQuestionValue || location.LocationAbbr) {
      setIsPatternsOverTime(false);
      fetchTableData(
        subQuestionValue,
        questionId,
        year || DEFAULT_YEAR || question.LatestYear,
        location.LocationAbbr
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.QuestionId, location, year, subQuestionValue]);

  async function getData(id, yr) {
    await setSubQuestionTableData(null);
    try {
      const response = await GetQuestionDataAllLocations({
        questionId: id,
        year: yr,
      });
      const questionData =
        response && response.data
          ? {
              tableData: response.data.tableData,
              medianRangeData: response.data.medianRangeData,
              legendData:
                response.data.mapData && response.data.mapData.length > 0
                  ? response.data.mapData[0].KeyItems
                  : null,
            }
          : null; //note data structure change
      setSubQuestionTableData(questionData);
      setSubQuestionCSVData(questionData);
      setFootnotes(await response.data.footnoteData);
      setShortNavFootNoteSymbol(await response.data.shortNavFootnoteSymbol);
    } finally {
      setOverlayVisible(false);
    }
  }

  function handleSubQuestionChange(obj) {
    setSubQuestionLoading(true);
    onVizTypeChange({
      vizType: vizType,
      questionId: question.QuestionId,
      subQuestionId: obj.SubQuestionId,
      locationAbbr: location.LocationAbbr,
    });
  }

  async function fetchPatternsOverTime() {
    await setSubQuestionTableData(null);
    try {
      const response = await GetPatternsOverTime({
        locationAbbr: location.LocationAbbr,
        questionId: question.QuestionId,
        // Use text matching to find the same question across years (SubQuestionIds change between survey years)
        subQuestionId: "",
        subQuestionText: subQuestion ? subQuestion.SubQuestion : "",
        // When no subquestion, fall back to SummaryText for matching
        summaryText: question ? question.SummaryText : "",
        matchSubQuestionByText: EnableSubQuestionTextMatching,
      });
      const questionData = response.data;
      setSubQuestionTableData(questionData);
      setSubQuestionCSVData(questionData);
      setFootnotes(await response.data.footnoteData);
    } finally {
      setOverlayVisible(false);
    }
  }

  async function handlePatternsOverTime(SubQuestionId) {
    onVizTypeChange({
      vizType: vizType,
      questionId: question.QuestionId,
      subQuestionId: SubQuestionId,
      locationAbbr: location.LocationAbbr,
    });
  }
  async function handleReturnToLocationList() {
    // Clear comparison data when navigating via breadcrumb
    setComparisonData(null);
    onVizTypeChange({
      vizType: vizType,
      questionId: question.QuestionId,
      subQuestionId: subQuestion ? subQuestion.SubQuestionId : null,
      locationAbbr: "ALL",
    });
    setIsPatternsOverTime(false);
  }

  async function handleReturnToLocationSubQuestionList() {
    analytics.selectTableReturn();
    // Clear comparison data when navigating via breadcrumb
    setComparisonData(null);
    onVizTypeChange({
      vizType: vizType,
      questionId: question.QuestionId,
      subQuestionId: null,
      locationAbbr: location.LocationAbbr,
    });
    setIsPatternsOverTime(false);
  }

  // Reset comparison when year or location context changes materially
  useEffect(() => {
    // Only keep comparison on All Locations table view for the same question/subquestion
    if (location.LocationAbbr !== "ALL" || vizType !== "table") {
      setComparisonData(null);
    }
  }, [location.LocationAbbr, vizType, question?.QuestionId, year]);

  useEffect(() => {
    if (vizType !== "graph") {
      setGraphCsvData(null);
    }
  }, [vizType]);

  // Clear comparison data when subquestion changes (separate effect to ensure it always triggers)
  useEffect(() => {
    setComparisonData(null);
  }, [subQuestionValue]);

  // Reset subquestion loading state when table data finishes loading
  useEffect(() => {
    if (subQuestionTableData !== null) {
      setSubQuestionLoading(false);
    }
  }, [subQuestionTableData]);

  async function fetchTableData(subQuestionId, questionId, year, locationAbbr) {
    if (locationAbbr === "ALL") {
      await fetchTableDataForAllLocation(subQuestionId, questionId, year);
    } else {
      await fetchTableDataForLocation(locationAbbr, questionId, year);
    }
  }

  async function fetchTableDataForLocation(locationAbbr, questionId, year) {
    await setSubQuestionTableData(null);
    try {
      const response = await GetQuestionOneLocation({
        locationAbbr,
        questionId,
        year,
      });

      //Modified to get table data from 'tableData' object else 'data'.
      //This is a modification to allow for getting footnote data in followup release
      let questionData =
        response.data.tableData && response.data.tableData.length
          ? response.data.tableData
          : response.data;

      if (questionData && questionData.length > 0) {
        //Set the table data
        setSubQuestionTableData(questionData);
        setSubQuestionCSVData(questionData);
        setFootnotes(await response.data.footnoteData);
        setShortNavFootNoteSymbol(await response.data.shortNavFootnoteSymbol);

        //Set the route. Let's get the parameters from the data
        const QuestionId = questionData[0].QuestionId;
        const LocationAbbr = questionData[0].LocationAbbr;
        // Keep the URL's year aligned with the requested year, rather than
        // whatever Yr the payload might contain, so the user's selection
        // remains the single source of truth for the year.
        navigate(
          // `?questionId=${QuestionId}&LocationAbbr=${LocationAbbr}&year=${year}`
          `?questionId=${QuestionId}&LocationAbbr=${LocationAbbr}`
        );
      } else {
        //Clear the table data for this context
        setSubQuestionTableData(null);
        setSubQuestionCSVData(null);

        // For visualization routes (table/map/graph), App.handleVizTypeChange
        // already pushed the correct URL. Avoid issuing an extra navigate here
        // in no-data scenarios to prevent redundant URL sync and flicker,
        // especially on the map view. Still allow explicit table-routing to
        // update the query string when needed.
        if (vizType === "table") {
          navigate(
            // `?questionId=${questionId}&LocationAbbr=${locationAbbr}&year=${year}`
            `?questionId=${questionId}&LocationAbbr=${locationAbbr}`
          );
        }
      }
    } finally {
      setOverlayVisible(false);
    }
  }

  async function fetchTableDataForAllLocation(subQuestionId, questionId, year) {
    await setSubQuestionTableData(null);
    try {
      const response = await GetSubQuestionAllLocations({
        subQuestionId,
        questionId,
        year,
      });

      //handles scenario where a question has subquestions and we need to show data driven "question" footnotes
      if (response && response.data && response.data.footnoteData) {
        setFootnotes(await response.data.footnoteData);
        setShortNavFootNoteSymbol(await response.data.shortNavFootnoteSymbol);
      }

      if (
        response &&
        response.data &&
        response.data.tableData &&
        response.data.tableData.length > 0
      ) {
        //if we have question data, do something.
        let questionData =
          response &&
          response.data &&
          response.data.tableData &&
          (await {
            tableData: response.data.tableData,
            medianRangeData: response.data.medianRangeData
              ? response.data.medianRangeData
              : null,
                legendData: response.data.mapData && response.data.mapData && response.data.mapData.length > 0
              ? response.data.mapData[0].KeyItems
              : null,
          }); //note data structure change
        //Set the table data
        setSubQuestionTableData(questionData);
        setSubQuestionCSVData(questionData);

        const QuestionId = questionData.tableData[0].QuestionId;
        const SubQuestionId = questionData.tableData[0].SubQuestionId;

        // Only adjust the query string from the data payload when we are in
        // table view (for deep-linking the exact data slice). On map/graph,
        // App.handleVizTypeChange already established the route, so avoid
        // an extra navigate that can cause visible flicker on fast clicks.
        if (vizType === "table") {
          navigate(
            // `?questionId=${QuestionId}&LocationAbbr=ALL${
            //   SubQuestionId ? "&subQuestionId=" + SubQuestionId : ""
            // }&year=${year}`
            `?questionId=${QuestionId}&LocationAbbr=ALL${SubQuestionId ? "&subQuestionId=" + SubQuestionId : ""}`
          );
        }
      } else {
        //Clear the table/map data for this subquestion/year
        setSubQuestionTableData(null);
        setSubQuestionCSVData(null);

        // In no-data scenarios, keep the existing map/table route that was
        // set by App.handleVizTypeChange. Do not issue another navigate so
        // that subQuestionId remains stable and we avoid hydration-like
        // flicker on the map when rapidly switching no-data subquestions.
        if (vizType === "table") {
          navigate(
            // `?questionId=${questionId}&LocationAbbr=ALL${
            //   subQuestionId ? "&subQuestionId=" + subQuestionId : ""
            // }&year=${year}`
            `?questionId=${questionId}&LocationAbbr=ALL${subQuestionId ? "&subQuestionId=" + subQuestionId : ""}`
          );
        }
      }
    } finally {
      setOverlayVisible(false);
    }
  }

  const buildCensusLookup = (rows) => {
    const map = new Map();
    (rows || []).forEach((row) => {
      const abbr = row?.LocationAbbr || row?.Location?.Abbr || row?.Location?.LocationAbbr;
      if (!abbr) return;
      if (isCensusSite(row)) map.set(String(abbr), true);
    });
    return map;
  };

  const fetchAllLocationsRows = async ({ effectiveSubQuestionId, yearVal }) => {
    if (effectiveSubQuestionId) {
      const response = await GetSubQuestionAllLocations({
        subQuestionId: effectiveSubQuestionId,
        questionId: question?.QuestionId,
        year: yearVal,
      });
      return response?.data?.tableData || response?.data || [];
    }

    const response = await GetQuestionDataAllLocations({
      questionId: question?.QuestionId,
      year: yearVal,
    });
    return response?.data?.tableData || response?.data || [];
  };
  //----------------------------------------------------------------

  const locationAbbr =
    location && location.LocationAbbr ? location.LocationAbbr : null;
  const subQuestionId =
    subQuestion && subQuestion.SubQuestionId ? subQuestion.SubQuestionId : null;
  const questionId =
    question && question.QuestionId ? question.QuestionId : null;
  const topicId = question ? question.TopicId : false;
  const showReturnToLocationView =
    !subQuestionValue ? true : false;
  const showReturnToSubQuestionView =
    question.SubQuestions.length > 0 &&
    subQuestionValue
      ? true
      : false;

  return (
    <>
      {/* Loading overlay for comparison fetches and subquestion loading */}
      <LoadingOverlay visible={overlayVisible || subQuestionLoading} text="Loading…" />
      <div className="row m-3" id="dataviz">
        <div className="col-12 p-0">
          <QuestionHeader
            comparisonData={comparisonData}
            displayLocations={displayLocations}
            displayLocationDropdown={displayLocationDropdown}
            handleSubQuestionChange={handleSubQuestionChange}
            isPatternsOverTime={isPatternsOverTime}
            locations={locations}
            onVizTypeChange={onVizTypeChange}
            onYearChange={onYearChange}
            onLocationChange={onLocationChange}
            setDataLoading={setDataLoading}
            subQuestionLoading={subQuestionLoading}
            onCompareSecondLocation={async (cmp) => {
              // cmp: { locationAbbr, year: compareYear }
              try {
                setOverlayVisible(true);
                const isAll = location.LocationAbbr === "ALL";
                const isLocationOnlyCompareView = !isAll && (isPatternsOverTime || !!subQuestion);
                // In POT mode without explicit subQuestionValue, extract SubQuestionId from POT data
                // This ensures the API returns all years for the comparison
                let effectiveSubQuestionId = subQuestionValue || null;
                let effectiveSubQuestionText = subQuestion?.SubQuestion || null;
                if (!effectiveSubQuestionId && isPatternsOverTime && subQuestionTableData?.tableData?.length > 0) {
                  effectiveSubQuestionId = subQuestionTableData.tableData[0]?.SubQuestionId || null;
                  effectiveSubQuestionText = subQuestionTableData.tableData[0]?.SubQuestion || null;
                }
                const compareLocationAbbr = cmp.locationAbbr || location.LocationAbbr;
                const defaultYear = Number(year || DEFAULT_YEAR || question.LatestYear);

                const fetchComparisonForYear = async (compareYr) => {
                  return await GetComparison({
                    questionId: question.QuestionId,
                    subQuestionId: effectiveSubQuestionId,
                    subQuestionText: effectiveSubQuestionText,
                    summaryText: question ? question.SummaryText : "",
                    year: compareYr,
                    compareYear: compareYr,
                    locationAbbr: location.LocationAbbr,
                    compareLocationAbbr,
                    includeLocationTypes: undefined,
                    includeNationwide: false,
                    alpha: 0.05,
                    matchSubQuestionByText: EnableSubQuestionTextMatching,
                  });
                };

                const normalizeYears = (payload) => {
                  const arr = Array.isArray(payload) ? payload : [];
                  return arr
                    .map((y) => (typeof y === "object" ? y.Year : y))
                    .map((y) => Number(y))
                    .filter((y) => Number.isFinite(y));
                };

                let res;
                if (isLocationOnlyCompareView) {
                  const [baseYearsRes, compareYearsRes] = await Promise.all([
                    GetYears({
                      questionId: question.QuestionId,
                      subQuestionId: effectiveSubQuestionId,
                      locationAbbr: location.LocationAbbr,
                      subQuestionText: effectiveSubQuestionText || "",
                      summaryText: question ? question.SummaryText : "",
                      matchByText: EnableSubQuestionTextMatching,
                    }),
                    GetYears({
                      questionId: question.QuestionId,
                      subQuestionId: effectiveSubQuestionId,
                      locationAbbr: compareLocationAbbr,
                      subQuestionText: effectiveSubQuestionText || "",
                      summaryText: question ? question.SummaryText : "",
                      matchByText: EnableSubQuestionTextMatching,
                    }),
                  ]);

                  const baseYears = normalizeYears(baseYearsRes?.data || baseYearsRes);
                  const compareYears = normalizeYears(compareYearsRes?.data || compareYearsRes);
                  const compareYearSet = new Set(compareYears);
                  const commonYears = Array.from(new Set(baseYears.filter((y) => compareYearSet.has(y)))).sort((a, b) => b - a);
                  const yearsToCompare = commonYears.length > 0
                    ? commonYears
                    : (Number.isFinite(defaultYear) ? [defaultYear] : []);

                  const comparisons = await Promise.all(
                    yearsToCompare.map((yr) => fetchComparisonForYear(yr))
                  );

                  const payloads = comparisons
                    .map((r) => (r && r.data ? r.data : r))
                    .filter(Boolean);
                  const baseMeta = payloads[0]?.Meta || {};
                  const mergedRows = payloads
                    .flatMap((p) => (Array.isArray(p?.Rows) ? p.Rows : []))
                    .filter((row) => row && row.YearA && row.YearB)
                    .sort((a, b) => Number(a?.YearA?.Year || 0) - Number(b?.YearA?.Year || 0));

                  res = {
                    data: {
                      ...(payloads[0] || {}),
                      Meta: {
                        ...baseMeta,
                        Year: yearsToCompare[0] || baseMeta.Year,
                        CompareYear: yearsToCompare[yearsToCompare.length - 1] || baseMeta.CompareYear,
                      },
                      Rows: mergedRows,
                    },
                  };
                } else {
                  res = await GetComparison({
                    questionId: question.QuestionId,
                    subQuestionId: effectiveSubQuestionId,
                    subQuestionText: effectiveSubQuestionText,
                    summaryText: question ? question.SummaryText : "",
                    year: year || DEFAULT_YEAR || question.LatestYear,
                    compareYear: cmp.year,
                    // For View 1 (ALL): keep state comparison across locations
                    // For View 2 (specific location): compare current location vs selected location across subquestions
                    locationAbbr: isAll ? "ALL" : location.LocationAbbr,
                    compareLocationAbbr: isAll ? undefined : compareLocationAbbr,
                    includeLocationTypes: isAll ? ["State", "Local"] : undefined,
                    includeNationwide: isAll ? true : false,
                    alpha: 0.05,
                    // Control text matching via env var
                    matchSubQuestionByText: EnableSubQuestionTextMatching,
                  });
                }
                // Attach labels for header rendering in View 2
                let enriched = res?.data || null;
                if (enriched && isAll) {
                  const baseRows = (subQuestionTableData && subQuestionTableData.tableData) ? subQuestionTableData.tableData : (Array.isArray(subQuestionTableData) ? subQuestionTableData : []);
                  const baseCensusLookup = buildCensusLookup(baseRows);
                  let compareCensusLookup = new Map();
                  try {
                    const compareRows = await fetchAllLocationsRows({
                      effectiveSubQuestionId,
                      yearVal: cmp.year,
                    });
                    compareCensusLookup = buildCensusLookup(compareRows);
                  } catch (_) {
                    compareCensusLookup = new Map();
                  }
                  const rows = Array.isArray(enriched.Rows) ? enriched.Rows : [];
                  enriched = {
                    ...enriched,
                    Rows: rows.map((row) => {
                      const abbr = row?.Location?.Abbr || row?.Location?.LocationAbbr || row?.Location?.Desc;
                      const baseCensus = abbr ? baseCensusLookup.get(String(abbr)) : false;
                      const compareCensus = abbr ? compareCensusLookup.get(String(abbr)) : false;
                      return {
                        ...row,
                        YearA: baseCensus ? { ...row.YearA, Census: "Y" } : row.YearA,
                        YearB: compareCensus ? { ...row.YearB, Census: "Y" } : row.YearB,
                      };
                    }),
                  };
                }
                if (enriched && !isAll) {
                  const baseRows = (subQuestionTableData && subQuestionTableData.tableData)
                    ? subQuestionTableData.tableData
                    : (Array.isArray(subQuestionTableData) ? subQuestionTableData : []);
                  const baseIsCensus = isCensusSite(location) || (baseRows || []).some((r) => isCensusSite(r));
                  const cmpLoc = (locations || []).find(l => l.LocationAbbr === (cmp.locationAbbr || location.LocationAbbr));
                  const compareIsCensus = isCensusSite(cmpLoc);
                  const labelA = location?.LocationDesc || location?.LocationAbbr || "";
                  const labelB = cmpLoc?.LocationDesc || cmpLoc?.LocationAbbr || (cmp.locationAbbr || "");
                  const abbrA = location?.LocationAbbr || "";
                  const abbrB = cmpLoc?.LocationAbbr || (cmp.locationAbbr || "");
                  const typeA = location?.LocationType || "";
                  const typeB = cmpLoc?.LocationType || "";
                  enriched = {
                    ...enriched,
                    Rows: (enriched.Rows || []).map((row) => ({
                      ...row,
                      YearA: baseIsCensus ? { ...(row?.YearA || {}), Census: "Y" } : row?.YearA,
                      YearB: compareIsCensus ? { ...(row?.YearB || {}), Census: "Y" } : row?.YearB,
                    })),
                    Meta: {
                      ...(enriched.Meta || {}),
                      LabelA: labelA,
                      LabelB: labelB,
                      AbbrA: abbrA,
                      AbbrB: abbrB,
                      TypeA: typeA,
                      TypeB: typeB,
                    }
                  };
                }
                setComparisonData(enriched);
              } catch (e) {
                // eslint-disable-next-line no-console
                console.error("Comparison fetch failed", e);
                setComparisonData(null);
              } finally {
                setOverlayVisible(false);
              }
            }}
            onReturnToLocationList={handleReturnToLocationList}
            onReturnToLocationSubQuestionList={
              handleReturnToLocationSubQuestionList
            }
            setComparisonData={setComparisonData}
            selectedData={selectedData}
            // Expose table sample size state to the header so the
            // SampleSizeToggle there controls the same flag used by Table.
            showSampleSize={showSampleSize}
            setShowSampleSize={setShowSampleSize}
            setLocationSelectedFromDropdownMenu={
              setLocationSelectedFromDropdownMenu
            }
            setIsPatternsOverTime={setIsPatternsOverTime}
            showReturnToLocationView={showReturnToLocationView}
            showReturnToSubQuestionView={showReturnToSubQuestionView}
            subQuestion={subQuestion}
            shortNavFootNoteSymbol={shortNavFootNoteSymbol}
            infoBannerMessage={infoBannerMessage}
          />

          {vizType === "table" && questionId && (
            <>
              <Table
                comparisonData={comparisonData}
                footnotes={footnotes}
                fetchTableData={fetchTableData}
                fetchTableDataForLocation={fetchTableDataForLocation}
                isPatternsOverTime={isPatternsOverTime}
                showSampleSize={showSampleSize}
                setShowSampleSize={setShowSampleSize}
                onVizTypeChange={onVizTypeChange}
                locationAbbr={locationAbbr}
                location={location}
                locationSelectedFromDropdownMenu={
                  locationSelectedFromDropdownMenu
                }
                onLocationChange={onLocationChange}
                onSubQuestionChange={handlePatternsOverTime}
                onCompareSecondLocation={async (cmp) => {
                  try {
                    setOverlayVisible(true);
                    const isAll = location.LocationAbbr === "ALL";
                    // In POT mode without explicit subQuestionValue, extract SubQuestionId from POT data
                    // This ensures the API returns all years for the comparison
                    let effectiveSubQuestionId = subQuestionValue || null;
                    let effectiveSubQuestionText = subQuestion?.SubQuestion || null;
                    if (!effectiveSubQuestionId && isPatternsOverTime && subQuestionTableData?.tableData?.length > 0) {
                      effectiveSubQuestionId = subQuestionTableData.tableData[0]?.SubQuestionId || null;
                      effectiveSubQuestionText = subQuestionTableData.tableData[0]?.SubQuestion || null;
                    }
                    const res = await GetComparison({
                      questionId: question.QuestionId,
                      subQuestionId: effectiveSubQuestionId,
                      subQuestionText: effectiveSubQuestionText,
                      summaryText: question ? question.SummaryText : "",
                      year: year || DEFAULT_YEAR || question.LatestYear,
                      compareYear: cmp.year,
                      locationAbbr: isAll ? "ALL" : location.LocationAbbr,
                      compareLocationAbbr: isAll ? undefined : (cmp.locationAbbr || location.LocationAbbr),
                      includeLocationTypes: isAll ? ["State", "Local"] : undefined,
                      includeNationwide: isAll ? true : false,
                      alpha: 0.05,
                      // Control text matching via env var
                      matchSubQuestionByText: EnableSubQuestionTextMatching,
                    });
                    // Attach labels for header rendering in View 2
                    let enriched = res?.data || null;
                    if (enriched && isAll) {
                      const baseRows = (subQuestionTableData && subQuestionTableData.tableData) ? subQuestionTableData.tableData : (Array.isArray(subQuestionTableData) ? subQuestionTableData : []);
                      const baseCensusLookup = buildCensusLookup(baseRows);
                      let compareCensusLookup = new Map();
                      try {
                        const compareRows = await fetchAllLocationsRows({
                          effectiveSubQuestionId,
                          yearVal: cmp.year,
                        });
                        compareCensusLookup = buildCensusLookup(compareRows);
                      } catch (_) {
                        compareCensusLookup = new Map();
                      }
                      const rows = Array.isArray(enriched.Rows) ? enriched.Rows : [];
                      enriched = {
                        ...enriched,
                        Rows: rows.map((row) => {
                          const abbr = row?.Location?.Abbr || row?.Location?.LocationAbbr || row?.Location?.Desc;
                          const baseCensus = abbr ? baseCensusLookup.get(String(abbr)) : false;
                          const compareCensus = abbr ? compareCensusLookup.get(String(abbr)) : false;
                          return {
                            ...row,
                            YearA: baseCensus ? { ...row.YearA, Census: "Y" } : row.YearA,
                            YearB: compareCensus ? { ...row.YearB, Census: "Y" } : row.YearB,
                          };
                        }),
                      };
                    }
                    if (enriched && !isAll) {
                      const baseRows = (subQuestionTableData && subQuestionTableData.tableData)
                        ? subQuestionTableData.tableData
                        : (Array.isArray(subQuestionTableData) ? subQuestionTableData : []);
                      const baseIsCensus = isCensusSite(location) || (baseRows || []).some((r) => isCensusSite(r));
                      const cmpLoc = (locations || []).find(l => l.LocationAbbr === (cmp.locationAbbr || location.LocationAbbr));
                      const compareIsCensus = isCensusSite(cmpLoc);
                      const labelA = location?.LocationDesc || location?.LocationAbbr || "";
                      const labelB = cmpLoc?.LocationDesc || cmpLoc?.LocationAbbr || (cmp.locationAbbr || "");
                      const abbrA = location?.LocationAbbr || "";
                      const abbrB = cmpLoc?.LocationAbbr || (cmp.locationAbbr || "");
                      const typeA = location?.LocationType || "";
                      const typeB = cmpLoc?.LocationType || "";
                      enriched = {
                        ...enriched,
                        Rows: (enriched.Rows || []).map((row) => ({
                          ...row,
                          YearA: baseIsCensus ? { ...(row?.YearA || {}), Census: "Y" } : row?.YearA,
                          YearB: compareIsCensus ? { ...(row?.YearB || {}), Census: "Y" } : row?.YearB,
                        })),
                        Meta: {
                          ...(enriched.Meta || {}),
                          LabelA: labelA,
                          LabelB: labelB,
                          AbbrA: abbrA,
                          AbbrB: abbrB,
                          TypeA: typeA,
                          TypeB: typeB,
                        }
                      };
                    }
                    setComparisonData(enriched);
                  } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error("Comparison fetch failed", e);
                    setComparisonData(null);
                  } finally {
                    setOverlayVisible(false);
                  }
                }}
                locations={locations}
                question={question}
                questionId={questionId}
                selectedData={selectedData}
                scrollToTableHeader={scrollToTableHeader}
                setIsPatternsOverTime={setIsPatternsOverTime}
                setSubQuestionTableData={setSubQuestionTableData}
                setSubQuestionCSVData={setSubQuestionCSVData}
                subQuestionId={subQuestionId}
                subQuestionTableData={subQuestionTableData}
                subQuestion={subQuestion}
                subQuestionValue={subQuestionValue}
                vizType={vizType}
                year={year}
              />
            </>
          )}


          {vizType === "map" && (
            <>
              {question && (
                <h2
                  className="mt-4 main-table-header question-header__location-text"
                  style={{
                    color: topicDictionary[question.TopicId].color,
                    fontSize: "30px",
                  }}
                >
                  {subQuestion ? subQuestion.SubQuestion : question.Question}
                </h2>
              )}

              <div className="mb-2 w-50 align-items-center mx-auto">
                <DataVisualizationToggle
                  onVizTypeChange={onVizTypeChange}
                  vizType={vizType}
                  question={question}
                  subQuestionValue={subQuestionValue}
                  locationAbbr={locationAbbr}
                  allowedOptions={["graph", "table", "map"]}
                />
              </div>

              <div className="map-sample-size-toggle">
                <SampleSizeToggle
                  id="mapShowSampleSizeCheckbox"
                  checked={showSampleSize}
                  onChange={(next) => setShowSampleSize(next)}
                  className="sample-size-toggle mb-0 d-inline-block"
                />
              </div>

              <MapView
                location={location}
                onVizTypeChange={onVizTypeChange}
                question={question}
                subQuestion={subQuestion}
                subQuestionCSVData={subQuestionCSVData}
                vizType={vizType}
                showSampleSizeOnMap={showSampleSize}
              />
            </>
          )}

          {vizType === "graph" && (
            <Graph
              location={location}
              onVizTypeChange={onVizTypeChange}
              question={question}
              subQuestion={subQuestion}
              vizType={vizType}
              subQuestionValue={subQuestionValue}
              locationAbbr={locationAbbr}
              year={year}
              setGraphCsvData={setGraphCsvData}
            />
          )}
        </div>
        <div>
          {(selectedData.question.SubQuestions.length === 0 ||
            selectedData.subQuestion !== null ||
            selectedData.location?.LocationAbbr !== "ALL") && (
            <div className="row">
              <div className="col-3">
                <PrintPdf
                  topicId={topicId}
                  topicIdSet={topicId !== undefined}
                />
              </div>
              <div className="col-3">
                <ExportCsv
                  subQuestionCSVData={subQuestionCSVData}
                  graphCsvData={graphCsvData}
                  comparisonData={comparisonData}
                  topicIdSet={topicId !== undefined}
                  topicId={topicId}
                  year={year}
                />
              </div>
            </div>
          )}
          
          <p />
          {(selectedData.question.SubQuestions.length === 0 ||
            selectedData.subQuestion !== null ||
            selectedData.location?.LocationAbbr !== "ALL") &&
            (vizType === "table" || vizType === "graph") &&
            (() => {
              const apiFootnotes = Array.isArray(footnotes) ? footnotes : [];
              const hasOriginalFootnotes = apiFootnotes.length > 0;
              const showPValueFootnote = vizType === "table" && !ShowPValueColumn;

              if (!hasOriginalFootnotes && !showPValueFootnote) return null;

              return (
                <div id="footnotes">
                  <h3>Footnotes</h3>
                  {hasOriginalFootnotes && (
                    <>
                      {apiFootnotes.map((i, idx) => (
                        <div
                          key={`${i?.DataValueFootnoteSymbol || "fn"}-${(i?.DataValueFootnote || "").slice(0, 25)}-${idx}`}
                          style={{ marginBottom: 7 }}
                        >
                          <span style={{ fontFamily: "arial", width: "35px" }}>
                            <span id="footnote-symbol">
                              {i.DataValueFootnoteSymbol}&nbsp;
                            </span>
                          </span>
                          <span id="footnote-text">{i.DataValueFootnote}</span>
                        </div>
                      ))}
                    </>
                  )}
                  {showPValueFootnote && (
                    <div style={{ marginBottom: 7 }}>
                      <span style={{ fontFamily: "arial", width: "35px" }}>
                        <span id="footnote-symbol">†&nbsp;</span>
                      </span>
                      <span id="footnote-text">Statistical significance is represented by a p-value of less than 0.05.</span>
                    </div>
                  )}
                  <hr />
                </div>
              );
            })()}
          <p />

          <div>
            <p className="m-0">
              <b>Last Reviewed:</b> January 22, 2026
            </p>
            <p>
              <b>Source:</b> Division of Adolescent and School Health, National
              Center for Chronic Disease Prevention and Health Promotion
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default DataVisualization;
